import { HttpClient, HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import { ConfigService } from './core/config.service';

/**
 * Service responsible for managing Accord Extraction file operations with SharePoint.
 *
 * Provides methods to:
 * - Upload business files to SharePoint
 * - Download template files from SharePoint
 *
 * This service communicates with the backend API which handles
 * authentication and SharePoint Graph integration.
 */
@Injectable({
  providedIn: 'root',
})
export class AccordExtractionSharePointService implements OnDestroy {
  private readonly _destroying$ = new Subject<void>();
  private readonly baseUrl: string;

  constructor(
    private http: HttpClient,
    private configService: ConfigService,
  ) {
    this.baseUrl = `${this.configService.config.backendUri}/sharepoint`;
  }

  ngOnDestroy(): void {
    this._destroying$.next();
    this._destroying$.complete();
  }

  /**
   * Downloads the SharePoint template file associated with the given filiale.
   *
   * This method sends a request to the backend API to retrieve the template file
   * stored in SharePoint. The response contains both the binary file content (Blob)
   * and HTTP headers (including Content-Disposition) used to extract the original
   * filename.
   *
   * Technical details:
   * - Uses HTTP GET with `observe: 'response'` to access response headers
   * - Returns a Blob wrapped inside an HttpResponse object
   * - Supports UTF-8 encoded filenames (RFC 5987)
   * - Automatically cancels the request when the service is destroyed
   *
   * Error handling:
   * - HTTP errors are caught and rethrown to be handled by the caller (component)
   *
   * @param filiale The filiale identifier used to resolve the SharePoint site and template mapping
   * @returns Observable<HttpResponse<Blob>> Observable containing the downloaded file and response metadata
   */
  downloadTemplate(filiale: string): Observable<HttpResponse<Blob>> {
    const params = new HttpParams().set('filiale', filiale);

    return this.http
      .get(`${this.baseUrl}/download-template`, {
        params,
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }

  /**
   * Uploads a file to SharePoint with business metadata used for naming and routing.
   *
   * This method sends a multipart/form-data request to the backend API which:
   * - Resolves the correct SharePoint site based on the filiale
   * - Applies the business naming convention to the uploaded file
   * - Stores the file in the appropriate SharePoint document library
   *
   * Technical details:
   * - Uses FormData to transmit both the file and business parameters
   * - Sends the request as a POST multipart/form-data payload
   * - Automatically unsubscribes when the service is destroyed
   *
   * Error handling:
   * - HTTP errors are caught and rethrown to be handled by the calling component
   *
   * @param file The file selected by the user to be uploaded
   * @param filiale Filiale identifier used to resolve SharePoint mapping and trigram
   * @param activite Business activity of the adherent (used in filename generation)
   * @param ville City of the adherent (used in filename generation)
   * @param nomOfficine Name of the officine (used in filename generation)
   *
   * @returns Observable<any> Observable containing the backend upload response
   */
  uploadFile(
    file: File,
    filiale: string,
    activite: string,
    ville: string,
    nomOfficine: string,
  ): Observable<any> {
    const formData = new FormData();

    formData.append('file', file);
    formData.append('filiale', filiale);
    formData.append('activite', activite);
    formData.append('ville', ville);
    formData.append('nomOfficine', nomOfficine);

    const url = `${this.baseUrl}/upload`;

    return this.http.post(url, formData).pipe(
      takeUntil(this._destroying$),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }

  /**
   * Sends a notification email to the GPO manager after a successful file upload.
   *
   * This method triggers an email notification containing the document link
   * to inform the responsible GPO manager about the newly uploaded file.
   *
   * Technical details:
   * - Sends a POST request to the backend email endpoint
   * - The backend resolves the appropriate recipient based on filiale
   * - Automatically unsubscribes when the service is destroyed
   *
   * Error handling:
   * - HTTP errors are caught and rethrown to be handled by the calling component
   *
   * @param filiale Filiale identifier used to determine the email recipient
   * @param nomOfficine Name of the officine (included in the email content)
   * @param lienDocument SharePoint URL of the uploaded document
   *
   * @returns Observable<void> Observable that completes when the email is sent
   */
  sendMailAfterUpload(
    filiale: string,
    nomOfficine: string,
    lienDocument: string,
  ): Observable<void> {
    const url = `${this.baseUrl}/send-mail`;
    return this.http
      .post<void>(url, {
        filiale,
        nomOfficine,
        lienDocument,
      })
      .pipe(
        takeUntil(this._destroying$),
        catchError((error: HttpErrorResponse) => throwError(() => error)),
      );
  }
}
